import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  getVisitorParkingSessionQueryKey,
  useGetVisitorParkingSession,
} from '@/api/visitor-parking/get-visitor-parking-session'
import { resetQueries } from '@/utils'

import {
  VISITOR_PARKING_TICKET_NUMBER_URL_KEY,
  VISITOR_PARKING_TOKEN_URL_KEY,
} from './visitor-parking.constants'
import { useVisitorParkingStore } from './visitor-parking.store'

interface UseInvalidateVisitorParkingSessionOnSSEProps {
  sessionId?: number
  ticketNumber: string
}

// Note: Stripe webhook with payment confirmation comes few seconds after the payment is processed.
// This hook will wait for an event and invalidate the session query to refetch the newest data.
export const useInvalidateVisitorParkingSessionOnSSE = ({
  sessionId,
  ticketNumber,
}: UseInvalidateVisitorParkingSessionOnSSEProps) => {
  const eventSourceRef = useRef<EventSource | null>(null)
  const [isConnected, setIsConnected] = useState(false)

  useEffect(() => {
    if (!sessionId) return

    const es = new EventSource(
      `${import.meta.env.VITE_API_URL}/sse?channels=parking-session-${sessionId}&public=1`,
    )

    es.onopen = () => {
      setIsConnected(true)
    }

    es.addEventListener('parking_session_created', () => {
      // Note: Invalidate the session query to refetch updated data
      resetQueries(getVisitorParkingSessionQueryKey({ ticketNumber }))
      es.close()
    })

    es.addEventListener('parking_session_extended', () => {
      // Note: Invalidate the session query to refetch updated data
      resetQueries(getVisitorParkingSessionQueryKey({ ticketNumber }))
      es.close()
    })

    es.onerror = () => {
      setIsConnected(false)
    }

    eventSourceRef.current = es

    return () => {
      es.close()
      eventSourceRef.current = null
    }
  }, [sessionId])

  return { isConnected }
}

export const useVisitorParkingAnalytics = () => {
  const [searchParams] = useSearchParams()
  const ticketNumber = searchParams.get(VISITOR_PARKING_TICKET_NUMBER_URL_KEY)
  const token = searchParams.get(VISITOR_PARKING_TOKEN_URL_KEY)
  const trackingSessionId = useVisitorParkingStore((state) => state.trackingSessionId)

  const { data: parkingSession } = useGetVisitorParkingSession({ ticketNumber: ticketNumber ?? '' })

  const handleTrackSupportRequest = () => {
    navigator.sendBeacon(
      `${import.meta.env.VITE_API_URL}/visitor-parking/analytics`,
      JSON.stringify({
        event: 'visitor_parking_support_clicked',
        tempSessionId: trackingSessionId,
        token,
        visitorParkingSessionId: parkingSession?.sessionId,
      }),
    )
  }

  const handleTrackFlowStarted = () => {
    navigator.sendBeacon(
      `${import.meta.env.VITE_API_URL}/visitor-parking/analytics`,
      JSON.stringify({
        event: 'visitor_parking_flow_started',
        tempSessionId: trackingSessionId,
        token,
        visitorParkingSessionId: parkingSession?.sessionId,
      }),
    )
  }

  return { handleTrackFlowStarted, handleTrackSupportRequest }
}
